import { createSharedComposable, useEventListener, useNow } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { IOdds } from '@/interface/IOdds';
import type { ITranslation } from '@/interface/ITranslation';
import { LanguageType, MarketType, PlayerType } from '@/interface/enum';
import { useApp } from '@/libraries/composables/useApp';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { getScorePredictionPromotionUrl } from '@/libraries/url';
import { useAccountStore } from '@/stores/accountStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

// Only ASI Asia-site B2C players with these currencies see the promotion entry (TCSP-8390).
const PROMOTION_CURRENCIES = ['TB', 'PP', 'VND', 'RM'];
// How often the start/end window is re-checked while the page stays open (same as Desktop TCSP-8392).
const PERIOD_CHECK_INTERVAL_MS = 30 * 1000;
const MAIN_MARKET_GROUP_ID = 0;

/**
 * One period clock per page, shared by every badge host and disposed when the last one unmounts.
 * Timers stall while a phone sleeps, so returning to the tab re-checks the period at once.
 * Only the clock is shared, not the store reads: PP / ES pages mount several Castor apps, each with its own Pinia.
 */
const usePromotionPeriodClock = createSharedComposable(() => {
    const now = useNow({ interval: PERIOD_CHECK_INTERVAL_MS });
    useEventListener(document, 'visibilitychange', () => {
        if (document.visibilityState === 'visible') now.value = new Date();
    });
    return now;
});

/**
 * Score Prediction promotion entry (TCSP-8390, Mobile). The feature toggle store only holds the parsed
 * settings; who is eligible, whether the period is on and which league / market qualifies is decided here.
 */
export function useScorePredictionPromotion() {
    const now = usePromotionPeriodClock();
    const { isAsi, isMixParlayMode } = storeToRefs(useRootStore());
    const { scorePredictionLeagueNames, scorePredictionPromotionPeriod, isEuroSite } = storeToRefs(useFeatureToggleStore());
    const { customerInfo } = storeToRefs(useAccountStore());
    const { isCurrencyInList } = useAccountStore();
    const { isFromApp, openUrlWithTitleInApp } = useApp();

    const isEligiblePlayer = computed(() => isAsi.value
        && !isEuroSite.value
        && customerInfo.value.playerType === PlayerType.B2C
        && isCurrencyInList(PROMOTION_CURRENCIES));

    const isInPeriod = computed(() => {
        const period = scorePredictionPromotionPeriod.value;
        return !!period && period.start <= now.value && now.value < period.end;
    });

    const isActive = computed(() => isEligiblePlayer.value
        && isInPeriod.value
        && !isMixParlayMode.value);

    function isConfiguredLeague(leagueName: ITranslation[]) {
        const leagueNameEn = getTranslated(leagueName, LanguageType.EN).trim().toUpperCase();
        return scorePredictionLeagueNames.value.some(name => name.toUpperCase() === leagueNameEn);
    }

    function isPromotionLeague(leagueName: ITranslation[]): boolean {
        return isActive.value && isConfiguredLeague(leagueName);
    }

    function isPromotionMarket(leagueName: ITranslation[], odds: Pick<IOdds, 'marketType' | 'marketGroup'>): boolean {
        const isFullTimeCorrectScore = odds.marketType === MarketType.CorrectScore && odds.marketGroup.id === MAIN_MARKET_GROUP_ID;
        return isFullTimeCorrectScore && isPromotionLeague(leagueName);
    }

    // Inside the App the page must not navigate the Sports webview away; the App opens it itself.
    // No title is sent — the App falls back to its own default title (Jason, 2026-10-07).
    function openEventPage(): void {
        const url = getScorePredictionPromotionUrl();
        if (isFromApp) {
            openUrlWithTitleInApp(url);
            return;
        }
        globalThis.location.href = url;
    }

    return { isPromotionLeague, isPromotionMarket, openEventPage };
}
